import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';

import { Button } from '@/components';
import { OfferEntryTable } from '@/features/offers/types';
import { cn } from '@/utils/styles';

import { OpportunityOffersItem } from './opportunity-offers-item';
import { ManageOfferDrawer } from '../../../../offers/components/drawer';

export type PaginatedOffersListProps = {
  readonly items: OfferEntryTable[];
  readonly itemsPerPage: number;
};

export function PaginatedOffersList({ items, itemsPerPage }: PaginatedOffersListProps) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedOffer, setSelectedOffer] = useState<OfferEntryTable | null>(null);

  const initialPage = searchParams.get('page') ? parseInt(searchParams.get('page') ?? '1') : 1;
  const [activePage, setActivePage] = useState(initialPage);
  const pages = Math.max(1, Math.ceil(items.length / itemsPerPage));

  useEffect(() => {
    if (activePage > pages) {
      updatePage(pages);
    }
  }, [pages]);

  const startIndex = (activePage - 1) * itemsPerPage;
  const endIndex = activePage * itemsPerPage;
  const displayItems = items.slice(startIndex, endIndex);
  const containerHeight = items.length >= 5 ? 'h-[250px]' : 'h-[173px]';

  const windowStart = activePage < 3 ? 0 : activePage > pages - 3 ? Math.max(pages - 5, 0) : activePage - 3;
  const paginationList = Array.from({ length: pages })
    .map((_, i) => i)
    .slice(windowStart, windowStart + 5);

  const onCloseDrawer = () => setSelectedOffer(null);

  const updatePage = (page: number) => {
    setActivePage(page);
    setSearchParams((searchParams) => {
      searchParams.set('page', page.toString());
      return searchParams;
    });
  };

  return (
    <div>
      <div className={containerHeight}>
        <OpportunityOffersItem items={displayItems} onItemClick={setSelectedOffer} />
      </div>
      <ManageOfferDrawer offer={selectedOffer} onClose={onCloseDrawer} />

      {pages >= 1 && (
        <div className="mt-8 flex justify-center">
          <Button
            onClick={() => updatePage(1)}
            color="neutral"
            className="bg-neutral-25 mr-2 enabled:hover:bg-neutral-50"
            disabled={activePage === 1}
          >
            <span>{'<<'}</span>
          </Button>
          {paginationList.map((index) => (
            <Button
              key={index}
              onClick={() => updatePage(index + 1)}
              color="neutral"
              className={cn(
                'bg-neutral-25 mr-2 enabled:hover:bg-neutral-50',
                index + 1 === activePage ? 'bg-neutral-50' : ''
              )}
              disabled={index + 1 === activePage}
            >
              {index + 1}
            </Button>
          ))}
          <Button
            onClick={() => updatePage(pages)}
            color="neutral"
            className="bg-neutral-25 mr-2 enabled:hover:bg-neutral-50"
            disabled={activePage === pages}
          >
            <span>{'>>'}</span>
          </Button>
        </div>
      )}
    </div>
  );
}
